import { useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader } from 'lucide-react'
import { useEffect, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import { z } from 'zod'
import { ToasterContent } from '@/components/sv/toaster-content'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { gql } from '@/lib/graphql/gql'
import type {
  RollWebhookSecretMutation,
  RollWebhookSecretMutationVariables,
} from '@/routes/webhook/tabs/overview/roll-webhook-secret-dialog/index.types.gen'

const ROLL_WEBHOOK_SECRET = gql`
  mutation RollWebhookSecret($id: String!, $expireInHours: Int) {
    rollWebhookSecret(id: $id, expireInHours: $expireInHours) {
      id
    }
  }
`

type Props = {
  isOpen: boolean
  onClose: () => void
}

const schema = z.object({
  expireInHours: z.number().int().min(0).max(24).default(0),
})

export const RollWebhookSecretDialog = ({ isOpen, onClose }: Props) => {
  const { idWebhook = '' } = useParams()
  const [isPending, startTransition] = useTransition()

  const [rollSecret] = useMutation<
    RollWebhookSecretMutation,
    RollWebhookSecretMutationVariables
  >(ROLL_WEBHOOK_SECRET)

  const form = useForm<z.input<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      expireInHours: 0,
    },
  })

  useEffect(() => {
    if (isOpen) {
      form.reset()
    }
  }, [isOpen, form])

  const onSubmit = async (values: z.input<typeof schema>) => {
    const isValid = await form.trigger()
    if (isPending || !isValid) {
      return
    }

    startTransition(async () => {
      try {
        await rollSecret({
          variables: {
            id: idWebhook,
            expireInHours: values.expireInHours,
          },
        })
        toast(
          <ToasterContent variant="success">
            Webhook secret is rolled successfully
          </ToasterContent>,
          { position: 'top-right' }
        )
        onClose()
      } catch {
        toast(
          <ToasterContent variant="error">
            Failed to rolle the webhook secret
          </ToasterContent>,
          { position: 'top-right' }
        )
      }
    })
  }

  return (
    <AlertDialog open={isOpen}>
      <AlertDialogContent onEscapeKeyDown={() => onClose()}>
        <AlertDialogHeader>
          <AlertDialogTitle>Roll webhook secret</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to roll the webhook secret?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="expireInHours"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="expireInHours">Expiration</FormLabel>
                  <FormControl>
                    <Select
                      value={String(field.value)}
                      onValueChange={(val) => field.onChange(Number(val))}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select an expiration" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Now</SelectItem>
                        <SelectItem value="1">1 hour</SelectItem>
                        <SelectItem value="2">2 hours</SelectItem>
                        <SelectItem value="4">4 hours</SelectItem>
                        <SelectItem value="8">8 hours</SelectItem>
                        <SelectItem value="16">16 hours</SelectItem>
                        <SelectItem value="24">24 hours</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormDescription>
                    Choose when the old secret should expire. After this time,
                    the webhook will use the new secret only.
                  </FormDescription>
                </FormItem>
              )}
            />
            <Button type="submit" hidden />
          </form>
        </Form>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending} onClick={() => onClose()}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={isPending}
            onClick={form.handleSubmit(onSubmit)}
          >
            {isPending ? <Loader className="animate-spin" /> : null}
            Continue
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
